import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationContainer } from './components/ui/NotificationContainer';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { MainLayout } from './components/layout/MainLayout';
import { LeaveRequests } from './pages/LeaveRequests';
import { NewLeaveRequest } from './pages/NewLeaveRequest';
import { LeaveRequestDetail } from './pages/LeaveRequestDetail';
import { Team } from './pages/Team';
import { UserManagement } from './pages/UserManagement';
import { UserForm } from './pages/UserForm';
import { Reports } from './pages/Reports';
import { TeamCalendar } from './pages/TeamCalendar';
import SystemNotifications from './pages/SystemNotifications';
import DepartmentManagement from './pages/DepartmentManagement';
import Substitutions from './pages/Substitutions';
import LeaveTypeManagement from './pages/LeaveTypeManagement';
import EditLeaveRequest from './pages/EditLeaveRequest';
import NotificationCenter from './pages/NotificationCenter';
import { RoleManagement } from './pages/RoleManagement';
import { WorkflowManagement } from './pages/WorkflowManagement';
import { WorkflowDetail } from './pages/WorkflowDetail';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { PasswordResetSuccess } from './pages/PasswordResetSuccess';
import { UserRedirect } from './components/UserRedirect';
import { TeamLeaveRequests } from './pages/TeamLeaveRequests';
import { MyLeaveRequests } from './pages/MyLeaveRequests';
import { UserSettings } from './pages/UserSettings';
import { Invoices } from './pages/accounting/Invoices';
import { Payments } from './pages/accounting/Payments';
import { Overdue } from './pages/accounting/Overdue';
import { AccountingReports } from './pages/accounting/AccountingReports';
import { ReminderConfig } from './pages/accounting/ReminderConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <NotificationContainer />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/password-reset-success" element={<PasswordResetSuccess />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/leaves" element={<MyLeaveRequests />} />
                <Route path="/leaves/team" element={<TeamLeaveRequests />} />
                <Route path="/leaves/new" element={<NewLeaveRequest />} />
                <Route path="/leaves/:id" element={<LeaveRequestDetail />} />
                <Route path="/leaves/:id/edit" element={<EditLeaveRequest />} />
                <Route path="/leave-types" element={<LeaveTypeManagement />} />
                <Route path="/team" element={<Team />} />
                <Route path="/calendar" element={<TeamCalendar />} />
                <Route path="/team-calendar" element={<TeamCalendar />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/users" element={<UserManagement />} />
                <Route path="/users/new" element={<UserForm />} />
                <Route path="/users/:id/edit" element={<UserForm />} />
                <Route path="/users/:id" element={<UserRedirect />} />
                <Route path="/notifications" element={<NotificationCenter />} />
                <Route path="/system-notifications" element={<SystemNotifications />} />
                <Route path="/departments" element={<DepartmentManagement />} />
                <Route path="/substitutions" element={<Substitutions />} />
                <Route path="/roles" element={<RoleManagement />} />
                <Route path="/workflows" element={<WorkflowManagement />} />
                <Route path="/workflows/:id" element={<WorkflowDetail />} />
                <Route path="/settings" element={<UserSettings />} />
                
                {/* Accounting Routes */}
                <Route path="/accounting/invoices" element={<Invoices />} />
                <Route path="/accounting/payments" element={<Payments />} />
                <Route path="/accounting/overdue" element={<Overdue />} />
                <Route path="/accounting/reports" element={<AccountingReports />} />
                <Route path="/accounting/reminder-config" element={<ReminderConfig />} />
                
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Route>
          </Routes>
          </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;